/* eslint-disable react/no-children-prop */
import { useContext, useEffect } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { Amount } from "app/components/ui/Amount/Amount";
import List from "app/pages/.shared/List.jsx";
import Separator from "app/pages/.shared/Separator";
import { PAYMENT_TERMS } from "app/constants";
import { isRecalculatingPricesForPayment } from "app/pages/Booking/Payment/paymentSelector";
import QuotationItemsContainer from "app/pages/Booking/Quotation/PriceDetails/QuotationItems/QuotationItemsContainer";
import Total from "app/pages/.shared/PriceSummary/Total/Total";
import TotalNote from "app/pages/.shared/PriceSummary/TotalNote/TotalNote";
import ExtraPriceDetailsContainer from "app/pages/Booking/Quotation/PriceDetails/ExtraPriceDetails/ExtraPriceDetailsContainer";
import "./PriceDetails.scss";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import FlexAsideButtonContainer from "app/pages/Booking/Quotation/FlexRate/FlexAsideButton";
import Collapse from "app/components/ui/collapse/Collapse";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import AccommodationsPricingContainer from "app/pages/HotelOnly/components/AccommodationsPricing/AccommodationsPricingContainer";
import TaxesFeesInfoContainer from "app/pages/Booking/Quotation/PriceDetails/TaxesFeesInfo/TaxesFeesInfoContainer";
import PaymentDueSummaryContainer from "app/pages/Booking/Quotation/PriceDetails/PaymentDueSummary/PaymentDueSummaryContainer";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import AppGlobalsContext from "app/AppGlobalsContext";
import BaggageInfoContainer from "app/pages/Booking/Quotation/PriceDetails/BaggageInfo/BaggageInfoContainer";
import SavingsHighlightContainer from "./SavingsHighlightContainer";
import { calculateDiscountPercentage } from "app/utils/utils";
import { ArrowDownIcon } from "app/icons/navigation";

const PriceDetails = ({
	total,
	savingsTotalAmount,
	hasChildren,
	included,
	notIncluded,
	isPagePayment,
	coutCredit,
	paymentMode,
	isBookingEligibleForDeposit,
	usableCouponsStatus,
	shouldHidePublicPrice = false,
	isHotelOnly = false,
}) => {
	const { track } = useAnalytics();

	const { resolution } = useContext(AppGlobalsContext);
	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;
	const isCalculatingPrices = isRecalculatingPricesForPayment(usableCouponsStatus, isPagePayment);

	const hasPriceDetails = included?.length > 0 || notIncluded?.length > 0;

	const totalWithCreditFees =
		paymentMode >= PAYMENT_TERMS["4x"] && isPagePayment ? total + coutCredit : total;
	const publicPrice = savingsTotalAmount > 0 ? total + savingsTotalAmount : 0;

	const discountPercentage = calculateDiscountPercentage({
		publicPrice,
		savingsTotalAmount,
	});

	useEffect(() => {
		if (total === 0) {
			track("total_to_pay_zero");
		}
	}, [total]);

	return (
		<div className="price-detail" data-testid="price-detail">
			<div className="price-detail__list">
				<div className="price-detail__list-content">
					<AccommodationsPricingContainer />
					<QuotationItemsContainer />
					<TaxesFeesInfoContainer />
					<BaggageInfoContainer />
					<ExtraPriceDetailsContainer />
				</div>
				<div className="price-detail__item" data-testid="price-detail-total">
					<Total
						total={totalWithCreditFees}
						discountPercentage={discountPercentage}
						isHotelOnly={isHotelOnly}
						isBookingEligibleForDeposit={isBookingEligibleForDeposit}
						isPagePayment={isPagePayment}
						paymentMode={paymentMode}
						isLoading={isCalculatingPrices}
						isPaymentSummaryLabel={true}
						taxesAndFeesIncluded={true}
					/>
				</div>
				<FlexAsideButtonContainer />
				<TotalNote showChildrenNote={hasChildren} />
				<div className="price-detail__footer">
					{hasPriceDetails && (
						<Collapse
							className="price-detail__collapse"
							expandIcon={({ isActive }) => {
								return (
									<ArrowDownIcon
										style={{
											transform: `rotate(${isActive ? 180 : 0}deg)`,
										}}
									/>
								);
							}}
						>
							<Collapse.Panel
								headerClass="price-detail__collapse-header"
								header={
									<div className="price-detail__collapse-header-content">
										<div className="price-detail__collapse-title">
											<FormattedMessage id="price.detail.included.show.more.label" />
										</div>
									</div>
								}
							>
								<div className="included">
									{included?.length > 0 && (
										<div className="included__list">
											<Typography
												variant={TYPOGRAPHY_VARIANTS.REGULAR}
												isBold
												className="included__title"
											>
												<FormattedMessage id="price.detail.included.title" />{" "}
												:
											</Typography>
											<List items={included} enableHTMLFormatting={true} />
										</div>
									)}

									{notIncluded?.length > 0 && (
										<div className="included__list">
											<Typography
												variant={TYPOGRAPHY_VARIANTS.REGULAR}
												isBold
												className="included__title"
											>
												<FormattedMessage id="price.detail.not.included.title" />{" "}
												:
											</Typography>
											<List items={notIncluded} enableHTMLFormatting={true} />
										</div>
									)}
								</div>
							</Collapse.Panel>
						</Collapse>
					)}
					{publicPrice > 0 && !shouldHidePublicPrice && !isHotelOnly && (
						<div className="price-detail__collapse-reference">
							<FormattedMessage
								id="general.instead.of.price.with.amount"
								values={{
									price: <Amount value={publicPrice} size="small" />,
								}}
							/>
						</div>
					)}
				</div>
				<SavingsHighlightContainer
					isHotelOnly={isHotelOnly}
					messageKey="general.savings.realized.with.amount"
					description={
						<div>
							<div className="savings-highlight__description-title">
								<FormattedMessage id="savings.tooltip.title" />
							</div>
							<ul className="savings-highlight__list">
								<li>
									<FormattedMessage id="savings.tooltip.line1" />
								</li>
								<li>
									<FormattedMessage id="savings.tooltip.line2" />
								</li>
							</ul>
						</div>
					}
					trackEvent="booking_click_savings_realized"
					trackData={{ publicPrice, discountPercentage }}
				/>
				{isPagePayment && !isMobile && (
					<div className="price-detail__payment-due-summary">
						<Separator margin="wide" />

						<PaymentDueSummaryContainer
							showPayNowBloc={false}
							isHotelOnly={isHotelOnly}
						/>
					</div>
				)}
			</div>
		</div>
	);
};

PriceDetails.propTypes = {
	total: PropTypes.number,
	savingsTotalAmount: PropTypes.number,
	isPagePayment: PropTypes.bool,
	hasChildren: PropTypes.bool,
	included: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
	notIncluded: PropTypes.arrayOf(PropTypes.string),
	coutCredit: PropTypes.number,
	paymentMode: PropTypes.number,
	usableCouponsStatus: PropTypes.string,
	isBookingEligibleForDeposit: PropTypes.bool,
	shouldHidePublicPrice: PropTypes.bool,
	isHotelOnly: PropTypes.bool,
};

export default PriceDetails;
